import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { slugMap } from "@/lib/blogContent";

const BLOG_POSTS = {
  en: [
    {
      id: 1,
      title: "Getting a Tattoo in Playa del Carmen: What You Need to Know",
      excerpt: "Everything tourists need to know before getting inked in paradise — from safety standards to booking tips and aftercare in tropical weather.",
      category: "Guide",
      readTime: "5 min",
    },
    {
      id: 2,
      title: "Mayan Tattoo Designs and Their Sacred Meanings",
      excerpt: "Discover the powerful symbolism behind traditional Mayan tattoo motifs — from Hunab Ku to the Jaguar and Quetzalcoatl.",
      category: "Culture",
      readTime: "7 min",
    },
    {
      id: 3,
      title: "Fine Line vs. Blackwork: Which Style Is Right for You?",
      excerpt: "A deep dive into two of the most requested tattoo styles of 2026, with examples from our studio to help you decide.",
      category: "Styles",
      readTime: "4 min",
    },
    {
      id: 4,
      title: "Piercing Aftercare in a Tropical Climate",
      excerpt: "Sun, saltwater, and humidity — how to properly care for your new piercing when you live at the beach.",
      category: "Aftercare",
      readTime: "3 min",
    },
    {
      id: 5,
      title: "Is It Safe to Get a Tattoo in Mexico?",
      excerpt: "Separating myth from reality: what safety actually looks like at a reputable studio in Playa del Carmen.",
      category: "Guide",
      readTime: "5 min",
    },
    {
      id: 6,
      title: "English-Speaking Tattoo Artists in Playa del Carmen",
      excerpt: "Custom tattoos require real conversation. Here's how to find an artist who speaks your language — literally.",
      category: "Guide",
      readTime: "4 min",
    },
    {
      id: 7,
      title: "How to Book a Tattoo Before Your Mexico Trip",
      excerpt: "Lock in your appointment before you land. A step-by-step guide to consulting, choosing your design, and arriving ready.",
      category: "Guide",
      readTime: "5 min",
    },
    {
      id: 8,
      title: "How Much Does a Tattoo Cost in Playa del Carmen?",
      excerpt: "Size, style, complexity, and artist experience all factor in. Here's what to expect — and how Mexico compares to the US and Europe.",
      category: "Guide",
      readTime: "5 min",
    },
  ],
  es: [
    {
      id: 1,
      title: "Tatuarte en Playa del Carmen: Lo Que Necesitas Saber",
      excerpt: "Todo lo que los turistas necesitan saber antes de tatuarse en el paraíso — estándares de seguridad, tips de reserva y cuidados en clima tropical.",
      category: "Guía",
      readTime: "5 min",
    },
    {
      id: 2,
      title: "Diseños de Tatuajes Mayas y Sus Significados Sagrados",
      excerpt: "Descubre el poderoso simbolismo detrás de los motivos mayas tradicionales — desde Hunab Ku hasta el Jaguar y Quetzalcóatl.",
      category: "Cultura",
      readTime: "7 min",
    },
    {
      id: 3,
      title: "Línea Fina vs. Blackwork: ¿Qué Estilo Va Contigo?",
      excerpt: "Un análisis profundo de dos de los estilos de tatuaje más solicitados de 2026, con ejemplos de nuestro estudio para ayudarte a decidir.",
      category: "Estilos",
      readTime: "4 min",
    },
    {
      id: 4,
      title: "Cuidados del Piercing en Clima Tropical",
      excerpt: "Sol, agua salada y humedad — cómo cuidar tu nuevo piercing cuando vives en la playa.",
      category: "Cuidados",
      readTime: "3 min",
    },
    {
      id: 5,
      title: "¿Es Seguro Tatuarse en México?",
      excerpt: "Separando mitos de realidad: qué significa realmente la seguridad en un estudio de calidad en Playa del Carmen.",
      category: "Guía",
      readTime: "5 min",
    },
    {
      id: 6,
      title: "Artistas de Tatuaje que Hablan Inglés en Playa del Carmen",
      excerpt: "Los tatuajes personalizados requieren conversación real. Así encuentras un artista que habla tu idioma, literalmente.",
      category: "Guía",
      readTime: "4 min",
    },
    {
      id: 7,
      title: "Cómo Reservar tu Tatuaje Antes de tu Viaje a México",
      excerpt: "Asegura tu cita antes de llegar. Guía paso a paso para consultar, elegir tu diseño y llegar listo.",
      category: "Guía",
      readTime: "5 min",
    },
    {
      id: 8,
      title: "¿Cuánto Cuesta un Tatuaje en Playa del Carmen?",
      excerpt: "Tamaño, estilo, complejidad y experiencia del artista influyen. Qué esperar — y cómo se compara México con EE.UU. y Europa.",
      category: "Guía",
      readTime: "5 min",
    },
  ],
};

export const Blog = ({ t, lang }) => {
  const posts = BLOG_POSTS[lang] || BLOG_POSTS.en;
  const labels = lang === "es"
    ? { eyebrow: "Nuestro Blog", title: "Tinta & Conocimiento", sub: "Guías, inspiración y todo sobre el mundo del tatuaje y piercing en el Caribe mexicano.", readMore: "Leer más", comingSoon: "Próximamente" }
    : { eyebrow: "Our Blog", title: "Ink & Insight", sub: "Guides, inspiration, and everything about tattoo and piercing culture in the Mexican Caribbean.", readMore: "Read more", comingSoon: "Coming soon" };

  return (
    <section
      id="blog"
      className="relative py-14 md:py-20 bg-bendito-bg border-t border-bendito-border"
      data-testid="blog-section"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12">
        <div className="mb-12 reveal">
          <div className="eyebrow mb-3">{labels.eyebrow}</div>
          <h2 className="font-heading text-bendito-ink text-4xl md:text-5xl lg:text-6xl uppercase tracking-tight">
            <span className="ink-underline">{labels.title}</span>
          </h2>
          <p className="text-bendito-muted mt-3 max-w-xl">{labels.sub}</p>
        </div>

        <div className="grid md:grid-cols-2 gap-5">
          {posts.map((post, i) => {
            const postSlug = slugMap[lang]?.[post.id];
            const hasFullPost = !!postSlug;
            const Wrapper = hasFullPost ? Link : "div";
            const wrapperProps = hasFullPost
              ? { to: `/${lang}/blog/${postSlug}` }
              : {};

            return (
              <Wrapper key={post.id} {...wrapperProps} className="block">
                <article
                  className={`group glass-card lift-card p-6 md:p-8 border border-bendito-border hover:border-bendito-orange/40 transition-colors reveal reveal-delay-${Math.min(i + 1, 3)} h-full`}
                >
                  <div className="flex items-center gap-3 mb-4">
                    <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-bendito-orange">
                      {post.category}
                    </span>
                    <span className="w-1 h-1 bg-bendito-muted rounded-full" />
                    <span className="font-mono text-[10px] tracking-[0.2em] text-bendito-muted">
                      {post.readTime}
                    </span>
                  </div>
                  <h3 className="font-heading text-bendito-ink text-xl md:text-2xl tracking-wide leading-tight mb-3 group-hover:text-bendito-orange transition-colors">
                    {post.title}
                  </h3>
                  <p className="text-bendito-muted text-sm leading-relaxed mb-5">
                    {post.excerpt}
                  </p>
                  <div className="flex items-center gap-2 font-mono text-[11px] tracking-[0.25em] uppercase text-bendito-ink/60 group-hover:text-bendito-orange transition-colors">
                    {hasFullPost ? labels.readMore : labels.comingSoon}
                    <ArrowUpRight size={14} className={`${hasFullPost ? "opacity-60 group-hover:opacity-100" : "opacity-0 group-hover:opacity-100"} transition-opacity`} />
                  </div>
                </article>
              </Wrapper>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Blog;
